import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Download,
  DollarSign,
  Package,
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  Loader2
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';
import { useSelector } from 'react-redux';
import { selectReportHref, selectCurrentClinic, selectClinicsLoading } from '@/store/slices/authSlice';
import { analyticsApi, type ReportResponse } from '@/services/analyticsService';

const Reports = () => {
  const reportHref = useSelector(selectReportHref);
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  const [dateRange, setDateRange] = useState('last-30-days');
  const [reportData, setReportData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert date range selection to API parameters
  const getDateRangeParams = (range: string) => {
    const endDate = new Date();
    const startDate = new Date();
    
    switch (range) {
      case 'last-7-days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last-30-days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last-90-days':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'last-year':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }
    
    return {
      start_date: startDate.toISOString().substring(0, 10),
      end_date: endDate.toISOString().substring(0, 10)
    };
  };

  useEffect(() => {
    const fetchReport = async () => {
      // Wait for clinics to load first
      if (clinicsLoading) {
        return;
      }

      if (!currentClinic) {
        setError('No clinic selected');
        setLoading(false);
        return;
      }

      // Hardcoded report data for UI demo
      const mockReportData: ReportResponse = {
        statistics: {
          total_revenue: {
            value: 84750,
            formatted_value: "$84,750",
            change: 18.5,
            change_direction: "up",
            change_period: "vs. last 30 days"
          },
          total_orders: {
            value: 412,
            change: 22.3,
            change_direction: "up",
            change_period: "vs. last 30 days"
          },
          average_order_value: {
            value: 205.70,
            formatted_value: "$205.70",
            change: 8.1,
            change_direction: "up",
            change_period: "vs. last 30 days"
          },
          top_medication: {
            name: "Metformin 500mg",
            orders: 89,
            revenue: 12450
          }
        },
        medication_analytics: [
          {
            medication: "Metformin 500mg",
            category: "Diabetes",
            orders: 89,
            revenue: "$12,450",
            percentage: 14.7,
            trend: "up"
          },
          {
            medication: "Lisinopril 10mg",
            category: "Hypertension",
            orders: 76,
            revenue: "$9,880",
            percentage: 11.7,
            trend: "up"
          },
          {
            medication: "Atorvastatin 20mg",
            category: "Cholesterol",
            orders: 68,
            revenue: "$8,630",
            percentage: 10.2,
            trend: "stable"
          },
          {
            medication: "Amlodipine 5mg",
            category: "Hypertension",
            orders: 62,
            revenue: "$7,440",
            percentage: 8.8,
            trend: "up"
          },
          {
            medication: "Omeprazole 20mg",
            category: "GI",
            orders: 58,
            revenue: "$6,960",
            percentage: 8.2,
            trend: "down"
          },
          {
            medication: "Levothyroxine 50mcg",
            category: "Thyroid",
            orders: 54,
            revenue: "$6,480",
            percentage: 7.6,
            trend: "up"
          },
          {
            medication: "Losartan 50mg",
            category: "Hypertension",
            orders: 48,
            revenue: "$5,760",
            percentage: 6.8,
            trend: "stable"
          },
          {
            medication: "Gabapentin 300mg",
            category: "Neurology",
            orders: 43,
            revenue: "$5,160",
            percentage: 6.1,
            trend: "up"
          },
          {
            medication: "Sertraline 50mg",
            category: "Mental Health",
            orders: 38,
            revenue: "$4,560",
            percentage: 5.4,
            trend: "down"
          },
          {
            medication: "Albuterol Inhaler",
            category: "Respiratory",
            orders: 35,
            revenue: "$4,200",
            percentage: 5.0,
            trend: "up"
          }
        ],
        links: {
          self: { href: "/clinic/123/reports" },
          export: { href: "/clinic/123/reports/export" }
        }
      };

      setReportData(mockReportData);
      setLoading(false);
      setError(null);

      // Original API call commented out for when endpoint is ready
      // if (!reportHref) {
      //   setError('Reports not available for this clinic');
      //   setLoading(false);
      //   return;
      // }

      // try {
      //   setLoading(true);
      //   setError(null);
      //   const dateParams = getDateRangeParams(dateRange);
      //   const data = await analyticsApi.getReport(reportHref, {
      //     ...dateParams,
      //     type: 'overview'
      //   });
      //   setReportData(data);
      // } catch (err) {
      //   setError(err instanceof Error ? err.message : 'Failed to load report');
      // } finally {
      //   setLoading(false);
      // }
    };

    fetchReport();
  }, [reportHref, dateRange, currentClinic, clinicsLoading]);

  const handleExportCSV = () => {
    if (!reportData) return;

    const csvContent = 'Medication,Orders,Revenue,Percentage,Category\n' +
      reportData.medication_analytics.map(med => 
        `${med.medication},${med.orders},${med.revenue},${med.percentage}%,${med.category}`
      ).join('\n');
    
    const filename = `medications-report-${new Date().toISOString().substring(0, 10)}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading || clinicsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 font-lato">
        <ResponsiveNavigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            <p className="text-vitl-dark/70 font-lato">
              {clinicsLoading ? 'Loading clinic data...' : 'Loading reports...'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 font-lato">
        <ResponsiveNavigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-red-600">
            <p>Error loading report: {error}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!reportData) return null;

  const { statistics, medication_analytics: medicationAnalytics } = reportData;

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-syne font-medium text-vitl-navy mb-2">Reports & Analytics</h1>
            <p className="text-vitl-dark/70 font-lato">Comprehensive insights into your clinic's performance and revenue.</p>
          </div>
          <div className="flex items-center space-x-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40 font-lato">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last-7-days">Last 7 days</SelectItem>
                <SelectItem value="last-30-days">Last 30 days</SelectItem>
                <SelectItem value="last-90-days">Last 90 days</SelectItem>
                <SelectItem value="last-year">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div className="flex items-center text-emerald-600">
                  <TrendingUp className="w-4 h-4" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-lato font-medium text-vitl-dark/70">Total Revenue</p>
                <p className="text-3xl font-lato font-bold text-vitl-navy">{statistics.total_revenue.formatted_value}</p>
                <div className="flex items-center space-x-1">
                  <span className={`text-sm font-medium ${statistics.total_revenue.change_direction === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {statistics.total_revenue.change_direction === 'up' ? '+' : '-'}{statistics.total_revenue.change.toFixed(1)}%
                  </span>
                  <span className="text-sm text-vitl-dark/50 font-lato">{statistics.total_revenue.change_period}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
                  <Package className="w-6 h-6" />
                </div>
                <div className="flex items-center text-emerald-600">
                  <TrendingUp className="w-4 h-4" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-lato font-medium text-vitl-dark/70">Total Orders</p>
                <p className="text-3xl font-lato font-bold text-vitl-navy">{statistics.total_orders.value}</p>
                <div className="flex items-center space-x-1">
                  <span className={`text-sm font-medium ${statistics.total_orders.change_direction === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {statistics.total_orders.change_direction === 'up' ? '+' : '-'}{statistics.total_orders.change}%
                  </span>
                  <span className="text-sm text-vitl-dark/50 font-lato">{statistics.total_orders.change_period}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <div className="flex items-center text-emerald-600">
                  <TrendingUp className="w-4 h-4" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-lato font-medium text-vitl-dark/70">Avg Order Value</p>
                <p className="text-3xl font-lato font-bold text-vitl-navy">{statistics.average_order_value.formatted_value}</p>
                <div className="flex items-center space-x-1">
                  <span className={`text-sm font-medium ${statistics.average_order_value.change_direction === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {statistics.average_order_value.change_direction === 'up' ? '+' : '-'}{statistics.average_order_value.change}%
                  </span>
                  <span className="text-sm text-vitl-dark/50 font-lato">{statistics.average_order_value.change_period}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
                  <Calendar className="w-6 h-6" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-lato font-medium text-vitl-dark/70">Top Medication</p>
                <p className="text-lg font-lato font-bold text-vitl-navy">{statistics.top_medication.name}</p>
                <div className="flex items-center space-x-1">
                  <span className="text-sm text-vitl-dark/50 font-lato">
                    {statistics.top_medication.orders} orders this period
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Medication Analytics */}
        <Card className="border-l-4 border-l-[#1b4736] shadow-sm hover:shadow-md transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-syne font-medium text-vitl-navy flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-[#1b4736]" />
              Medication Revenue Analysis
            </CardTitle>
            <Button
              onClick={handleExportCSV}
              className="bg-[#1b4736] hover:bg-[#1b4736]/90 text-white font-lato font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-lato font-medium">Medication</TableHead>
                  <TableHead className="font-lato font-medium">Category</TableHead>
                  <TableHead className="font-lato font-medium">Orders</TableHead>
                  <TableHead className="font-lato font-medium">Revenue</TableHead>
                  <TableHead className="font-lato font-medium">% of Total</TableHead>
                  <TableHead className="font-lato font-medium">Trend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {medicationAnalytics.map((med, index) => (
                  <TableRow key={index} className="hover:bg-gray-50">
                    <TableCell className="font-medium font-lato text-vitl-navy">{med.medication}</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-lato text-xs">
                        {med.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-lato text-vitl-dark/70">{med.orders}</TableCell>
                    <TableCell className="font-lato font-medium text-vitl-navy">{med.revenue}</TableCell>
                    <TableCell className="font-lato text-vitl-dark/70">{med.percentage.toFixed(1)}%</TableCell>
                    <TableCell>
                      <div className={`flex items-center ${
                        med.trend === 'up' ? 'text-emerald-600' : 
                        med.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {med.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : 
                         med.trend === 'down' ? <TrendingDown className="w-4 h-4" /> : 
                         <span className="text-sm">—</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Reports;